import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import PrintIcon from '@mui/icons-material/PrintOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import LineItemsDialog from '@/components/common/LineItemsDialog';
import type { DataTableColumn } from '@/components/common/DataTable';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import StatusChip from '@/components/common/StatusChip';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import { PERMISSIONS } from '@/types/auth';
import type { CompletePackingRequest, PackageDto, PackageInput, PackingTaskDto } from '@/types/outbound';

const emptyPackage: PackageInput = { packageNo: '', materialId: '', carrierId: null, weight: null, remarks: null, qty: 0 };

export default function PackingPage() {
  const { t } = useTranslation('packing');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: processConfiguration } = useProcessConfiguration();
  const packingLabelEnabled = processConfiguration?.packingLabelEnabled ?? true;

  // Every task, not just Pending — a Completed task needs to stay visible here so its Print
  // action (see below) is reachable, matching where the operator already is after packing it.
  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['packing-tasks'],
    queryFn: async () => (await apiClient.get<PackingTaskDto[]>('/packing-tasks')).data,
  });

  const [selected, setSelected] = useState<PackingTaskDto | null>(null);
  const [packages, setPackages] = useState<PackageInput[]>([{ ...emptyPackage }]);
  const [viewing, setViewing] = useState<PackingTaskDto | null>(null);

  const openTask = (task: PackingTaskDto) => {
    setSelected(task);
    setPackages(
      task.lines.length > 0
        ? task.lines.map((l, idx) => ({
            packageNo: `${task.packingTaskNumber}-${idx + 1}`,
            materialId: l.materialId,
            carrierId: null,
            weight: null,
            remarks: null,
            qty: l.issuedQty,
          }))
        : [{ ...emptyPackage, packageNo: `${task.packingTaskNumber}-1` }],
    );
  };

  // Per-material caps read from the task's own source line (DispatchOrder or PickList, whichever
  // it has) — a package's qty can never push a material's allocated total past its issued qty.
  const lineByMaterial = useMemo(() => new Map(selected?.lines.map((l) => [l.materialId, l]) ?? []), [selected]);
  const allocatedByMaterial = useMemo(() => {
    const m = new Map<string, number>();
    packages.forEach((p) => {
      if (p.materialId) m.set(p.materialId, (m.get(p.materialId) ?? 0) + (p.qty || 0));
    });
    return m;
  }, [packages]);
  const overAllocatedMaterialIds = useMemo(() => {
    const s = new Set<string>();
    allocatedByMaterial.forEach((qty, materialId) => {
      const line = lineByMaterial.get(materialId);
      if (line && qty > line.issuedQty) s.add(materialId);
    });
    return s;
  }, [allocatedByMaterial, lineByMaterial]);
  const hasLines = Boolean(selected && selected.lines.length > 0);
  const missingMaterial = hasLines && packages.some((p) => !p.materialId);
  const invalid = overAllocatedMaterialIds.size > 0 || missingMaterial;

  const completeMutation = useMutation({
    mutationFn: () => {
      const payload: CompletePackingRequest = { packages: packages.filter((p) => p.packageNo && p.qty > 0) };
      return apiClient.post(`/packing-tasks/${selected!.id}/complete`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packing-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['loading-tasks'] });
      setSelected(null);
    },
  });

  const columns: DataTableColumn<PackingTaskDto>[] = [
    { field: 'packingTaskNumber', headerName: t('table.number') },
    { field: 'source', headerName: t('table.source'), render: (r) => r.orderNumber ?? r.pickListNumber ?? '—' },
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'action', headerName: t('table.actions'), render: (r) => (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          {r.status === 'Completed' && (
            <Button size="small" onClick={() => setViewing(r)}>{t('view')}</Button>
          )}
          <IfPermitted permission={PERMISSIONS.PACKING_EXECUTE}>
            {r.status === 'Completed' ? (
              packingLabelEnabled && (
                <Button
                  size="small" variant="outlined" startIcon={<PrintIcon fontSize="small" />}
                  onClick={() => navigate(`/transactions/packing-label-printing?taskId=${r.id}`)}
                >
                  {t('print')}
                </Button>
              )
            ) : (
              <Button size="small" variant="outlined" onClick={() => openTask(r)}>{t('pack')}</Button>
            )}
          </IfPermitted>
        </Stack>
      ),
    },
  ];

  const packageColumns: DataTableColumn<PackageDto>[] = [
    { field: 'packageNo', headerName: t('dialog.packageNo') },
    { field: 'materialCode', headerName: t('dialog.material'), render: (p) => (p.materialCode ? `${p.materialCode} — ${p.materialName}` : '—') },
    { field: 'qty', headerName: t('dialog.qty') },
    { field: 'weight', headerName: t('dialog.weight'), render: (p) => p.weight ?? '—' },
    { field: 'carrierName', headerName: t('dialog.carrier'), render: (p) => p.carrierName ?? '—' },
  ];

  return (
    <>
      <PageHeader title={t('title')} subtitle={t('subtitle')} />

      <DataTable columns={columns} rows={tasks} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="packing-tasks" />

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        {selected && (
          <>
            <DialogTitle>{selected.packingTaskNumber}</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                {selected.lines.length > 0 && (
                  <Stack spacing={1}>
                    <Typography variant="subtitle2">{t('dialog.lineItems')}</Typography>
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('dialog.materialCode')}</TableCell>
                          <TableCell>{t('dialog.materialName')}</TableCell>
                          <TableCell align="right">{t('dialog.requestedQty')}</TableCell>
                          <TableCell align="right">{t('dialog.issuedQty')}</TableCell>
                          <TableCell align="right">{t('dialog.allocatedQty')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selected.lines.map((line) => {
                          const allocated = allocatedByMaterial.get(line.materialId) ?? 0;
                          return (
                            <TableRow key={line.materialId}>
                              <TableCell>{line.materialCode}</TableCell>
                              <TableCell>{line.materialName}</TableCell>
                              <TableCell align="right">{line.requestedQty}</TableCell>
                              <TableCell align="right">{line.issuedQty}</TableCell>
                              <TableCell align="right" sx={{ color: allocated > line.issuedQty ? 'error.main' : undefined }}>
                                {allocated}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                      </Table>
                    </Box>
                  </Stack>
                )}

                {packages.map((pkg, idx) => (
                  <GridContainer key={idx} spacing={1.5} columns={12}>
                    <GridItem xs={12} sm={3}>
                      <TextField label={t('dialog.packageNo')} value={pkg.packageNo} size="small" fullWidth disabled />
                    </GridItem>
                    <GridItem xs={12} sm={4}>
                      <TextField
                        select label={t('dialog.material')} value={pkg.materialId} size="small" fullWidth
                        disabled={!hasLines}
                        onChange={(e) => setPackages((prev) => prev.map((p, i) => (i === idx ? { ...p, materialId: e.target.value } : p)))}
                      >
                        {!hasLines && <MenuItem value="">{t('dialog.selectMaterial')}</MenuItem>}
                        {selected.lines.map((line) => (
                          <MenuItem key={line.materialId} value={line.materialId}>
                            {line.materialCode} — {line.materialName}
                          </MenuItem>
                        ))}
                      </TextField>
                    </GridItem>
                    <GridItem xs={6} sm={2}>
                      <TextField
                        type="number" label={t('dialog.qty')} value={pkg.qty} size="small" fullWidth
                        error={Boolean(pkg.materialId) && overAllocatedMaterialIds.has(pkg.materialId)}
                        onChange={(e) => setPackages((prev) => prev.map((p, i) => (i === idx ? { ...p, qty: Number(e.target.value) } : p)))}
                      />
                    </GridItem>
                    <GridItem xs={6} sm={2}>
                      <TextField
                        type="number" label={t('dialog.weight')} value={pkg.weight ?? ''} size="small" fullWidth
                        onChange={(e) => setPackages((prev) => prev.map((p, i) => (i === idx ? { ...p, weight: e.target.value === '' ? null : Number(e.target.value) } : p)))}
                      />
                    </GridItem>
                    <GridItem xs={12} sm={1}>
                      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <IconButton size="small" onClick={() => setPackages((prev) => prev.filter((_, i) => i !== idx))} disabled={packages.length <= 1}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </GridItem>
                  </GridContainer>
                ))}
                <Button
                  size="small" startIcon={<AddIcon />} sx={{ alignSelf: 'flex-start' }}
                  onClick={() => setPackages((prev) => {
                    const nextLine = selected.lines.find((l) => l.issuedQty - (allocatedByMaterial.get(l.materialId) ?? 0) > 0);
                    const remaining = nextLine ? nextLine.issuedQty - (allocatedByMaterial.get(nextLine.materialId) ?? 0) : 0;
                    return [
                      ...prev,
                      { ...emptyPackage, packageNo: `${selected.packingTaskNumber}-${prev.length + 1}`, materialId: nextLine?.materialId ?? '', qty: remaining },
                    ];
                  })}
                >
                  {t('dialog.addPackage')}
                </Button>

                {missingMaterial && <Alert severity="error">{t('dialog.materialRequired')}</Alert>}
                {overAllocatedMaterialIds.size > 0 && <Alert severity="error">{t('dialog.overAllocated')}</Alert>}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setSelected(null)}>{t('cancel')}</Button>
              <Button variant="contained" disabled={invalid || completeMutation.isPending} onClick={() => completeMutation.mutate()}>
                {t('dialog.complete')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <LineItemsDialog
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        title={viewing?.packingTaskNumber ?? ''}
        subtitle={viewing ? (viewing.orderNumber ?? viewing.pickListNumber ?? undefined) : undefined}
        columns={packageColumns}
        rows={viewing?.packages ?? []}
      />
    </>
  );
}
